import { useMemo } from 'react';
import { useSkuReadiness } from '../../api/skuReadiness.ts';
import { useT } from '../../i18n/index.ts';
import { Chip, DropdownMenu } from '../../ui/index.ts';
import { ReadinessChecklist, readinessItems } from './ReadinessChecklist.tsx';

/**
 * Page-header readiness chip with the checklist folded into its popover
 * (#1428).
 *
 * Until now the header chip answered "is this SKU routable" from the targets
 * table while the page-bottom checklist answered the same question from the
 * gateway's readiness endpoint. Two reads, two derivations, one claim — and
 * on dev they disagreed, because the targets table sees rows the readiness
 * bits reject (dead probe, missing procurement cost). This chip now reads the
 * SAME batched endpoint the marketplace list column reads (#1335), so the
 * summary and its detail cannot drift apart, and the market list and the
 * detail page cannot either.
 */
export function ReadinessChip({
  skuId,
  priced,
  visible,
}: {
  readonly skuId: string;
  readonly priced: boolean;
  readonly visible: boolean;
}): JSX.Element {
  const t = useT();
  // One SKU is a one-element batch of the marketplace list's read. Sharing it
  // keeps a single readiness client — and, because the query key stays under
  // `SKU_READINESS_QUERY_KEY`, keeps this chip inside the invalidation the
  // SKU-target mutations issue.
  const skuIds = useMemo(() => [skuId], [skuId]);
  const readiness = useSkuReadiness(skuIds);
  const bits = readiness.bitsBySku.get(skuId);

  if (readiness.isPending) {
    return (
      <Chip tone="neutral" lamp={false} testId="sku-detail-readiness">
        {t('sku_detail.readiness.checking')}
      </Chip>
    );
  }

  // A resolved batch that omits this SKU is as unusable as a failed one: the
  // three gateway bits would silently read as false and the checklist would
  // invent a blocker the server never reported.
  if (readiness.isError || bits === undefined) {
    return (
      <Chip tone="bad" testId="sku-detail-readiness">
        {t('sku_detail.readiness.unknown')}
      </Chip>
    );
  }

  const items = readinessItems({ bits, priced, visible });
  const blocked = items.filter((item) => !item.ready).length;

  return (
    <DropdownMenu
      align="start"
      testId="sku-detail-readiness-popover"
      trigger={
        <button
          type="button"
          className="inline-flex rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
          data-testid="sku-detail-readiness-trigger"
        >
          <Chip tone={blocked === 0 ? 'ok' : 'warn'} testId="sku-detail-readiness">
            {blocked === 0
              ? t('sku_detail.readiness.ready')
              : t('sku_detail.checklist.blocked_count', { count: String(blocked) })}
          </Chip>
        </button>
      }
    >
      <div className="w-[min(34rem,80vw)]">
        <p className="m-0 px-3 py-2 text-label text-paper-low">{t('sku_detail.checklist.title')}</p>
        <ReadinessChecklist items={items} />
      </div>
    </DropdownMenu>
  );
}
